// eslint-disable-next-line no-unused-vars
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSave,
  faSpinner,
  faUserEdit,
} from "@fortawesome/free-solid-svg-icons";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

// Authentication credentials (same as parent component)
const username = "admin1";
const password = "abc@123@Q";
const authHeader = "Basic " + btoa(`${username}:${password}`);

// Function to get CSRF token (reused from parent)
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Authorization: authHeader,
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const EditCustomer = ({
  customer,
  isOpen,
  onClose,
  onSave,
  mutateCustomers,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    email: "",
    phone: "",
    address: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form when customer data changes or modal opens
  useEffect(() => {
    if (customer && isOpen) {
      setFormData({
        title: customer.attributes?.title || "",
        email: customer.attributes?.field_customer_email || "",
        phone: customer.attributes?.field_phone_number || "",
        address: customer.attributes?.field_customer_address || "",
      });
      setError(null);
    }
  }, [customer, isOpen]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      setError("Customer name is required");
      return;
    }

    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const csrfToken = await getCsrfToken();
      if (!csrfToken) {
        setError("Authentication failed. Please refresh the page.");
        setIsLoading(false);
        return;
      }

      // Prepare the data in Drupal JSON:API format
      const updateData = {
        data: {
          type: "node--customer",
          id: customer.id,
          attributes: {
            title: formData.title,
            field_customer_email: formData.email,
            field_phone_number: formData.phone,
            field_customer_address: formData.address,
          },
        },
      };

      // Send PATCH request to update customer
      const response = await fetch(
        `${process.env.REACT_APP_CUSTOMER_API}/${customer.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: authHeader,
            "Content-Type": "application/vnd.api+json",
            Accept: "application/vnd.api+json",
            "X-CSRF-Token": csrfToken,
          },
          credentials: "include",
          body: JSON.stringify(updateData),
        },
      );

      if (response.ok) {
        const updatedCustomer = await response.json();

        // Refresh the customer list
        if (mutateCustomers) {
          mutateCustomers();
        }

        // Call the onSave callback if provided
        if (onSave) {
          onSave(updatedCustomer);
        }

        // Close the modal
        onClose();
      } else {
        let errorMessage = `Failed to update customer. Status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage =
            errorData.message || errorData.errors?.[0]?.title || errorMessage;
        } catch (e) {
          // Ignore parsing error
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error("Error updating customer:", err);
      setError(
        "An error occurred while updating the customer. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <div style={styles.headerTitle}>
            <FontAwesomeIcon icon={faUserEdit} style={styles.headerIcon} />
            <h3 style={styles.modalTitle}>Edit Customer</h3>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit}>
          <div style={styles.modalBody}>
            {/* Error Message */}
            {error && (
              <div style={styles.errorMessage}>
                <span style={styles.errorText}>{error}</span>
              </div>
            )}

            {/* Customer Name Field */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Customer Name <span style={styles.requiredStar}>*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                style={styles.input}
                placeholder="Enter customer name"
                disabled={isLoading}
              />
            </div>

            {/* Email Field */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Email Address <span style={styles.requiredStar}>*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                placeholder="customer@example.com"
                disabled={isLoading}
              />
            </div>

            {/* Phone Number Field */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                style={styles.input}
                placeholder="Enter phone number"
                disabled={isLoading}
              />
            </div>

            {/* Address Field */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Address</label>
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                style={styles.textarea}
                placeholder="Enter customer address"
                rows="3"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Modal Footer */}
          <div style={styles.modalFooter}>
            <button
              type="button"
              style={styles.cancelButton}
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={styles.saveButton}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    style={{ marginRight: "8px" }}
                  />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faSave}
                    style={{ marginRight: "8px" }}
                  />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Styles for the modal
const styles = {
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    backdropFilter: "blur(4px)",
  },
  modalContainer: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "550px",
    maxHeight: "90vh",
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    animation: "slideIn 0.3s ease-out",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#f8f9fa",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  headerIcon: {
    fontSize: "20px",
    color: "#007bff",
  },
  modalTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#999",
    padding: "4px 8px",
    borderRadius: "4px",
    transition: "all 0.2s",
    ":hover": {
      backgroundColor: "#e9ecef",
      color: "#333",
    },
  },
  modalBody: {
    padding: "24px",
    maxHeight: "calc(90vh - 140px)",
    overflowY: "auto",
  },
  formGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#555",
  },
  requiredStar: {
    color: "#dc3545",
    marginLeft: "4px",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    transition: "border-color 0.2s, box-shadow 0.2s",
    outline: "none",
    boxSizing: "border-box",
    ":focus": {
      borderColor: "#007bff",
      boxShadow: "0 0 0 2px rgba(0, 123, 255, 0.1)",
    },
    ":disabled": {
      backgroundColor: "#f8f9fa",
      cursor: "not-allowed",
    },
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    transition: "border-color 0.2s, box-shadow 0.2s",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    resize: "vertical",
    ":focus": {
      borderColor: "#007bff",
      boxShadow: "0 0 0 2px rgba(0, 123, 255, 0.1)",
    },
    ":disabled": {
      backgroundColor: "#f8f9fa",
      cursor: "not-allowed",
    },
  },
  errorMessage: {
    backgroundColor: "#f8d7da",
    border: "1px solid #f5c6cb",
    borderRadius: "6px",
    padding: "12px",
    marginBottom: "20px",
  },
  errorText: {
    color: "#721c24",
    fontSize: "14px",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "16px 24px",
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#f8f9fa",
  },
  cancelButton: {
    padding: "8px 20px",
    fontSize: "14px",
    fontWeight: "500",
    backgroundColor: "#fff",
    color: "#6c757d",
    border: "1px solid #ced4da",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    ":hover": {
      backgroundColor: "#e9ecef",
      borderColor: "#adb5bd",
    },
    ":disabled": {
      opacity: 0.6,
      cursor: "not-allowed",
    },
  },
  saveButton: {
    padding: "8px 20px",
    fontSize: "14px",
    fontWeight: "500",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ":hover": {
      backgroundColor: "#0056b3",
    },
    ":disabled": {
      backgroundColor: "#6c757d",
      cursor: "not-allowed",
    },
  },
};

// Add animation keyframes (can be added to global CSS or CSS-in-JS library)
// For simplicity, we'll add a style tag if needed, but typically you'd add this to your global CSS
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  `;
  if (!document.querySelector("#edit-customer-animations")) {
    styleSheet.id = "edit-customer-animations";
    document.head.appendChild(styleSheet);
  }
}

export default EditCustomer;
